import React, { useMemo } from "react";
import {
  Stack,
  Group,
  Select,
  Button,
  Text,
  TextInput,
  ActionIcon,
  Box,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons";
import {
  MCA_ENTITIES,
  MCA_USER_VALUE_SOURCES,
  approvalRoleLabel,
  approvalRoleOptions,
  getEntityLabel,
  getEntitySetValueFields,
  getRelatedSetValueTargets,
  userEntityRoleOptions,
} from "../../data/mcaEntities";
import { getMcaWatchFieldMeta } from "../../helpers/mcaWatchFieldMeta";
import { buildWatchFieldSelectOptions } from "./mcaWatchFieldLabels";
import McaConditionValueInput from "./McaConditionValueInput";
import McaConditionRefValueInput from "./McaConditionRefValueInput";
import { useMcaEditorReadOnly } from "./mcaEditorReadOnly";

const SELF_ENTITY = "__self__";

const NON_SCALAR_SET_FIELDS = new Set(["incomeSources", "aml", "rpq", "files"]);
const DERIVED_WATCH_FIELDS = new Set(["clientClass"]);

const FIELD_SELECT_STYLES = {
  dropdown: {
    minWidth: "max-content",
    width: "max-content",
    maxWidth: "min(90vw, 560px)",
  },
  item: {
    whiteSpace: "nowrap",
  },
};

const FIELD_SELECT_Z_INDEX = 5000;

const isSingleUserSetField = (entityKey, field) => {
  if (!entityKey || !field) return false;
  const meta = getMcaWatchFieldMeta(entityKey, field);
  return (
    meta.valueType === "ref" &&
    meta.refConfig?.apiEntity === "user" &&
    meta.refConfig?.isArrayRef !== true
  );
};

const withCurrentOption = (options, value) => {
  if (!value || options.some((option) => option.value === value)) return options;
  return [...options, { value, label: value }];
};

const ownEntityFieldOptions = (entityKey) => {
  const entity = MCA_ENTITIES.find((item) => item.value === entityKey);
  const watchFields = (entity?.watchFields ?? []).filter(
    (field) =>
      field && !NON_SCALAR_SET_FIELDS.has(field) && !DERIVED_WATCH_FIELDS.has(field)
  );
  const options = buildWatchFieldSelectOptions(entityKey, watchFields);
  const known = new Set(options.map((option) => option.value));
  for (const item of getEntitySetValueFields(entityKey)) {
    if (!item.field || known.has(item.field) || NON_SCALAR_SET_FIELDS.has(item.field)) {
      continue;
    }
    options.push({ value: item.field, label: item.label || item.field });
  }
  return options;
};

const McaSetValuesEditor = ({
  setValues = [],
  onChange,
  statusFields = [],
  targetEntity,
  steps = [],
  description = "Applied to the target entity when this step is approved.",
}) => {
  const readOnly = useMcaEditorReadOnly();
  const relatedTargets = getRelatedSetValueTargets(targetEntity);
  const ownFieldOptions = useMemo(() => {
    if (targetEntity) return ownEntityFieldOptions(targetEntity);
    return statusFields.map((item) => ({
      value: item.field,
      label: item.label,
    }));
  }, [targetEntity, statusFields]);

  const updateRow = (idx, patch) => {
    if (readOnly) return;
    const next = setValues.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };

  const relatedFieldsForRow = (row) =>
    relatedTargets.find((target) => target.entity === row.targetEntity)?.fields ?? [];

  const fieldDef = (row) => {
    if (!row.targetEntity) return null;
    const fields = relatedFieldsForRow(row);
    return (
      fields.find((item) => item.field === row.field) ??
      (row.field ? { field: row.field, label: row.field, options: [] } : null)
    );
  };

  const fieldSelectData = (row) => {
    if (row.targetEntity) {
      const fields = relatedFieldsForRow(row);
      const known = fields.map((item) => ({
        value: item.field,
        label: item.label,
      }));
      if (row.field && !fields.some((item) => item.field === row.field)) {
        known.push({ value: row.field, label: row.field });
      }
      return known;
    }
    const known = [...ownFieldOptions];
    if (row.field && !known.some((item) => item.value === row.field)) {
      known.push({ value: row.field, label: row.field });
    }
    return known;
  };

  const statusValueOptions = (row) => {
    if (row.targetEntity) {
      return fieldDef(row)?.options ?? [];
    }
    return statusFields.find((item) => item.field === row.field)?.options ?? [];
  };

  const entitySelectData = [
    {
      value: SELF_ENTITY,
      label: getEntityLabel(targetEntity) || "This record",
    },
    ...relatedTargets.map((target) => ({
      value: target.entity,
      label: target.label,
    })),
  ];

  if (!ownFieldOptions.length && !relatedTargets.length) {
    return (
      <Text size="xs" color="dimmed">
        Select a target entity with status fields to configure set values.
      </Text>
    );
  }

  return (
    <Stack spacing="xs" sx={{ width: "100%" }}>
      {description ? (
        <Text size="xs" color="dimmed">
          {description}
        </Text>
      ) : null}
      {setValues.map((row, idx) => {
        const def = fieldDef(row);
        const valueFromLabel = def?.valueFrom ? def.valueLabel || def.valueFrom : "";
        const useEntityValue = Boolean(targetEntity) && !row.targetEntity && !valueFromLabel;
        return (
          <Group key={idx} spacing="sm" align="flex-end" noWrap sx={{ width: "100%" }}>
            {relatedTargets.length > 0 && (
              <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                <Select
                  placeholder="Record"
                  size="xs"
                  sx={{ width: "100%" }}
                  data={entitySelectData}
                  value={row.targetEntity || SELF_ENTITY}
                  onChange={(v) =>
                    updateRow(idx, {
                      targetEntity: !v || v === SELF_ENTITY ? "" : v,
                      field: "",
                      value: "",
                      valueFrom: "",
                    })
                  }
                  readOnly={readOnly}
                  searchable
                />
              </Box>
            )}
            <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
              <Select
                placeholder="Field"
                size="xs"
                sx={{ width: "100%" }}
                data={fieldSelectData(row)}
                value={row.field || null}
                onChange={(v) => {
                  const nextField = v ?? "";
                  const nextDef = relatedFieldsForRow({
                    ...row,
                    field: nextField,
                  }).find((item) => item.field === nextField);
                  const userField =
                    !row.targetEntity && isSingleUserSetField(targetEntity, nextField);
                  updateRow(idx, {
                    field: nextField,
                    value: "",
                    valueFrom: row.targetEntity ? nextDef?.valueFrom || "" : "",
                    valueSource: userField ? "USER" : "",
                  });
                }}
                readOnly={readOnly}
                searchable
                clearable
                withinPortal
                zIndex={FIELD_SELECT_Z_INDEX}
                maxDropdownHeight={320}
                dropdownComponent="div"
                styles={FIELD_SELECT_STYLES}
                nothingFound="No matching fields"
              />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
              {valueFromLabel ? (
                <TextInput
                  placeholder="Value"
                  size="xs"
                  sx={{ width: "100%" }}
                  value={valueFromLabel}
                  readOnly
                />
              ) : useEntityValue && isSingleUserSetField(targetEntity, row.field) ? (
                <UserSetValueInput
                  row={row}
                  targetEntity={targetEntity}
                  steps={steps}
                  readOnly={readOnly}
                  onChange={(patch) => updateRow(idx, patch)}
                />
              ) : useEntityValue && row.field ? (
                <McaConditionValueInput
                  label=""
                  size="xs"
                  entityKey={targetEntity}
                  field={row.field}
                  conditionType="$eq"
                  value={row.value ?? ""}
                  onChange={(next) => updateRow(idx, { value: next ?? "" })}
                />
              ) : useEntityValue ? (
                <TextInput placeholder="Value" size="xs" sx={{ width: "100%" }} disabled value="" />
              ) : (
                <Select
                  placeholder="Value"
                  size="xs"
                  sx={{ width: "100%" }}
                  data={statusValueOptions(row)}
                  value={row.value ?? ""}
                  onChange={(v) => updateRow(idx, { value: v ?? "" })}
                  searchable
                  clearable
                  readOnly={readOnly}
                  disabled={!row.field}
                />
              )}
            </Box>
            {!readOnly && (
            <ActionIcon
              variant="subtle"
              size="sm"
              sx={{ flexShrink: 0 }}
              aria-label="Remove set value"
              onClick={() => onChange(setValues.filter((_, i) => i !== idx))}
            >
              <IconTrash size={14} />
            </ActionIcon>
            )}
          </Group>
        );
      })}
      {!readOnly && (
      <Button
        size="xs"
        w="fit-content"
        variant="light"
        leftIcon={<IconPlus size={14} />}
        onClick={() => onChange([...setValues, { field: "", value: "" }])}
      >
        Add set value
      </Button>
      )}
    </Stack>
  );
};

const UserSetValueInput = ({ row, targetEntity, steps, readOnly, onChange }) => {
  const source = row.valueSource || "USER";
  const meta = getMcaWatchFieldMeta(targetEntity, row.field);
  const entityRoles = withCurrentOption(userEntityRoleOptions(targetEntity), row.value);
  const approvalRoles = approvalRoleOptions(steps);
  const approvalData =
    row.value && !approvalRoles.some((option) => option.value === row.value)
      ? [
          ...approvalRoles,
          { value: row.value, label: approvalRoleLabel(row.value, steps) || row.value },
        ]
      : approvalRoles;

  return (
    <Stack spacing={4} sx={{ width: "100%" }}>
      <Select
        aria-label="Assign from"
        placeholder="Assign from"
        size="xs"
        sx={{ width: "100%" }}
        data={MCA_USER_VALUE_SOURCES}
        value={source}
        onChange={(next) => {
          if (!next) return;
          onChange({ valueSource: next, value: "" });
        }}
        readOnly={readOnly}
        withinPortal
        zIndex={FIELD_SELECT_Z_INDEX}
      />
      {source === "ENTITY_ROLE" ? (
        <Select
          aria-label="Entity role"
          placeholder="Entity role"
          size="xs"
          sx={{ width: "100%" }}
          data={entityRoles}
          value={row.value || null}
          onChange={(next) => onChange({ value: next ?? "" })}
          readOnly={readOnly}
          searchable
          clearable
          withinPortal
          zIndex={FIELD_SELECT_Z_INDEX}
        />
      ) : source === "APPROVAL_ROLE" ? (
        <Select
          aria-label="Approval role"
          placeholder="Approval role"
          size="xs"
          sx={{ width: "100%" }}
          data={approvalData}
          value={row.value || null}
          onChange={(next) => onChange({ value: next ?? "" })}
          readOnly={readOnly}
          searchable
          clearable
          withinPortal
          zIndex={FIELD_SELECT_Z_INDEX}
        />
      ) : (
        <McaConditionRefValueInput
          label=""
          size="xs"
          refConfig={meta.refConfig}
          conditionType="$eq"
          value={row.value ?? ""}
          onChange={(next) => onChange({ value: next ?? "", valueSource: "USER" })}
          disabled={readOnly}
        />
      )}
    </Stack>
  );
};

export default McaSetValuesEditor;
